/* ===========================================================================
   Descriptores de los catálogos CRUD genéricos.
   Cada entrada configura una pestaña de Catalogos.vue, renderizada por el
   componente reutilizable CrudResource.vue (lista + alta/edición + borrado).

   scope:
     "none"     → la lista se acota por tenant en el backend (forUser).
     "politica" → se inyecta politica_id (de la sesión) en el filtro de la
                  lista y en el cuerpo de creación; la entidad no se muestra
                  si no hay política activa.

   columns[].type: "text" (def) | "bool" | "money" | "ref" (resuelve el nombre
   desde la lista de opciones cargada para ese campo).

   fields[].type: "text" | "number" | "decimal" | "email" | "textarea" | "date"
                | "select" (options: [valor, etiqueta][])
                | "ref"    (ref: { endpoint, label, scope? })
                | "toggle"
   =========================================================================== */

const CLASIFICACION = [
  ["salario_base", "Salario base"],
  ["complemento_personal", "Complemento personal"],
  ["complemento_puesto", "Complemento de puesto"],
  ["variable", "Variable"],
  ["especie", "Especie"],
  ["extrasalarial", "Extrasalarial"],
  ["indemnizacion", "Indemnización"],
];

const NATURALEZA = [
  ["efectivo", "Efectivo"],
  ["especie", "En especie"],
];

const ESTADO_PROYECTO = [
  ["activo", "Activo"],
  ["en_curso", "En curso"],
  ["cerrado", "Cerrado"],
  ["archivado", "Archivado"],
];

const clienteRef = { endpoint: "/clientes", label: "nombre" };
const proyectoRef = { endpoint: "/proyectos", label: "nombre" };
const personaRef = { endpoint: "/personas", label: "nombre", scope: "politica" };
const tipoConceptoRef = { endpoint: "/tipos-concepto", label: "nombre", scope: "politica" };
const familiaConceptoRef = { endpoint: "/familias-concepto", label: "nombre" };

export const CATALOGOS = [
  {
    key: "clientes",
    label: "Clientes",
    singular: "Cliente",
    endpoint: "/clientes",
    scope: "none",
    columns: [
      { key: "nombre", label: "Cliente" },
      { key: "cif", label: "CIF" },
      { key: "sector", label: "Sector" },
      { key: "n_empleados_aprox", label: "Nº empleados" },
      { key: "contacto_email", label: "Contacto" },
      { key: "activo", label: "Activo", type: "bool" },
    ],
    fields: [
      { key: "nombre", label: "Nombre", type: "text", required: true },
      { key: "cif", label: "CIF", type: "text" },
      { key: "sector", label: "Sector", type: "text" },
      { key: "n_empleados_aprox", label: "Nº de empleados aprox.", type: "number" },
      { key: "contacto_email", label: "Email de contacto", type: "email" },
      { key: "activo", label: "Activo", type: "toggle", default: true },
    ],
  },
  {
    key: "proyectos",
    label: "Proyectos",
    singular: "Proyecto",
    endpoint: "/proyectos",
    scope: "none",
    columns: [
      { key: "nombre", label: "Proyecto" },
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef },
      { key: "ejercicio", label: "Ejercicio" },
      { key: "estado_label", label: "Estado" },
    ],
    fields: [
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef, required: true },
      { key: "nombre", label: "Nombre", type: "text", required: true },
      { key: "ejercicio", label: "Ejercicio", type: "number", required: true, default: new Date().getFullYear() },
      { key: "estado", label: "Estado", type: "select", options: ESTADO_PROYECTO, default: "activo" },
      { key: "descripcion", label: "Descripción", type: "textarea" },
    ],
  },
  {
    key: "politicas-retributivas",
    label: "Políticas retributivas",
    singular: "Política retributiva",
    endpoint: "/politicas-retributivas",
    scope: "none",
    refreshSession: true,
    columns: [
      { key: "ejercicio", label: "Ejercicio" },
      { key: "nombre", label: "Política" },
      { key: "proyecto_id", label: "Proyecto", type: "ref", ref: proyectoRef },
      { key: "activa", label: "Activa", type: "bool" },
    ],
    fields: [
      { key: "proyecto_id", label: "Proyecto", type: "ref", ref: proyectoRef, required: true },
      { key: "nombre", label: "Nombre", type: "text", required: true },
      { key: "ejercicio", label: "Ejercicio", type: "number", required: true, default: new Date().getFullYear() },
      { key: "umbral_alerta", label: "Umbral de alerta de brecha (%)", type: "decimal", default: 25 },
      { key: "umbral_ue", label: "Umbral Directiva UE (%)", type: "decimal", default: 5 },
      { key: "formula_brecha", label: "Fórmula de brecha", type: "text", default: "(H-M)/H*100" },
      { key: "dias_computables", label: "Días computables del ejercicio", type: "number", default: 365 },
      { key: "jornada_completa_horas", label: "Horas de jornada completa (anual)", type: "decimal", default: 1776 },
      { key: "equiparar_jornada", label: "Equiparar a jornada completa", type: "toggle", default: true },
      { key: "excluir_indemnizaciones", label: "Excluir indemnizaciones", type: "toggle", default: true },
      { key: "banda_base", label: "Banda · base (€, corte en VPT 0)", type: "decimal", default: 14000 },
      { key: "banda_pendiente", label: "Banda · pendiente (€/punto VPT)", type: "decimal", default: 155 },
      { key: "banda_factor_inf", label: "Banda · factor cota inferior", type: "decimal", default: 0.8 },
      { key: "banda_factor_sup", label: "Banda · factor cota superior", type: "decimal", default: 1.2 },
      { key: "activa", label: "Activa", type: "toggle", default: true },
    ],
  },
  {
    key: "centros-trabajo",
    label: "Centros de trabajo",
    singular: "Centro de trabajo",
    endpoint: "/centros-trabajo",
    scope: "none",
    columns: [
      { key: "nombre", label: "Centro" },
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef },
      { key: "provincia", label: "Provincia" },
    ],
    fields: [
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef, required: true },
      { key: "nombre", label: "Nombre", type: "text", required: true },
      { key: "provincia", label: "Provincia", type: "text" },
    ],
  },
  {
    key: "departamentos",
    label: "Departamentos",
    singular: "Departamento",
    endpoint: "/departamentos",
    scope: "none",
    columns: [
      { key: "nombre", label: "Departamento" },
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef },
    ],
    fields: [
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef, required: true },
      { key: "nombre", label: "Nombre", type: "text", required: true },
    ],
  },
  {
    key: "categorias",
    label: "Categorías profesionales",
    singular: "Categoría profesional",
    endpoint: "/categorias",
    scope: "none",
    columns: [
      { key: "codigo", label: "Código" },
      { key: "nombre", label: "Categoría" },
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef },
      { key: "grupo", label: "Grupo profesional" },
      { key: "orden", label: "Orden" },
    ],
    fields: [
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef, required: true },
      { key: "codigo", label: "Código", type: "text", required: true },
      { key: "nombre", label: "Nombre", type: "text", required: true },
      { key: "grupo", label: "Grupo profesional (convenio)", type: "text" },
      { key: "orden", label: "Orden", type: "number", default: 0 },
    ],
  },
  {
    key: "familias-concepto",
    label: "Familias de concepto",
    singular: "Familia de concepto",
    endpoint: "/familias-concepto",
    scope: "none",
    columns: [
      { key: "codigo", label: "Código" },
      { key: "nombre", label: "Familia" },
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef },
      { key: "orden", label: "Orden" },
    ],
    fields: [
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef, required: true },
      { key: "codigo", label: "Código", type: "text", required: true },
      { key: "nombre", label: "Nombre", type: "text", required: true },
      { key: "orden", label: "Orden", type: "number", default: 0 },
    ],
  },
  {
    key: "grupos-convenio",
    label: "Grupos de convenio",
    singular: "Grupo de convenio",
    endpoint: "/grupos-convenio",
    scope: "none",
    columns: [
      { key: "codigo", label: "Código" },
      { key: "nombre", label: "Grupo" },
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef },
      { key: "orden", label: "Orden" },
    ],
    fields: [
      { key: "cliente_id", label: "Cliente", type: "ref", ref: clienteRef, required: true },
      { key: "codigo", label: "Código", type: "text", required: true },
      { key: "nombre", label: "Nombre", type: "text", required: true },
      { key: "orden", label: "Orden", type: "number", default: 0 },
    ],
  },
  {
    key: "grupos-profesionales",
    label: "Grupos profesionales",
    singular: "Grupo profesional",
    endpoint: "/grupos-profesionales",
    scope: "politica",
    columns: [
      { key: "codigo", label: "Código" },
      { key: "nombre", label: "Grupo" },
      { key: "pts_min", label: "Pts mín." },
      { key: "pts_max", label: "Pts máx." },
      { key: "orden", label: "Orden" },
    ],
    fields: [
      { key: "codigo", label: "Código", type: "text", required: true },
      { key: "nombre", label: "Nombre", type: "text", required: true },
      { key: "pts_min", label: "Puntos mínimos", type: "number", required: true, default: 0 },
      { key: "pts_max", label: "Puntos máximos", type: "number" },
      { key: "orden", label: "Orden", type: "number", required: true, default: 0 },
    ],
  },
  {
    key: "bandas-tiv",
    label: "Bandas TIV",
    singular: "Banda TIV",
    endpoint: "/bandas-tiv",
    scope: "politica",
    columns: [
      { key: "codigo", label: "Código" },
      { key: "nombre", label: "Banda" },
      { key: "pts_min", label: "Pts mín." },
      { key: "pts_max", label: "Pts máx." },
      { key: "orden", label: "Orden" },
    ],
    fields: [
      { key: "codigo", label: "Código", type: "text", required: true },
      { key: "nombre", label: "Nombre", type: "text", required: true },
      { key: "pts_min", label: "Puntos mínimos", type: "number", required: true, default: 0 },
      { key: "pts_max", label: "Puntos máximos", type: "number" },
      { key: "orden", label: "Orden", type: "number", required: true, default: 0 },
    ],
  },
  {
    key: "tipos-concepto",
    label: "Tipos de concepto",
    singular: "Tipo de concepto",
    endpoint: "/tipos-concepto",
    scope: "politica",
    columns: [
      { key: "nombre", label: "Concepto" },
      { key: "clasificacion_label", label: "Clasificación" },
      { key: "familia_id", label: "Familia", type: "ref", ref: familiaConceptoRef },
      { key: "naturaleza", label: "Naturaleza" },
      { key: "normalizable", label: "Normalizable", type: "bool" },
      { key: "anualizable", label: "Anualizable", type: "bool" },
      { key: "computa_total", label: "Computa total", type: "bool" },
      { key: "orden", label: "Orden" },
    ],
    fields: [
      { key: "nombre", label: "Nombre", type: "text", required: true },
      { key: "clasificacion", label: "Clasificación", type: "select", options: CLASIFICACION, required: true, default: "salario_base" },
      { key: "familia_id", label: "Familia", type: "ref", ref: familiaConceptoRef },
      { key: "naturaleza", label: "Naturaleza", type: "select", options: NATURALEZA, required: true, default: "efectivo" },
      { key: "normalizable", label: "Normalizable a jornada completa", type: "toggle", default: false },
      { key: "anualizable", label: "Anualizable al periodo", type: "toggle", default: false },
      { key: "computa_total", label: "Computa en el total", type: "toggle", default: true },
      { key: "vigencia_inicio", label: "Vigente desde", type: "date" },
      { key: "vigencia_fin", label: "Vigente hasta", type: "date" },
      { key: "orden", label: "Orden", type: "number", required: true, default: 0 },
    ],
  },
  {
    key: "competencias",
    label: "Competencias",
    singular: "Competencia",
    endpoint: "/competencias",
    scope: "politica",
    columns: [
      { key: "nombre", label: "Competencia" },
      { key: "orden", label: "Orden" },
      { key: "activa", label: "Activa", type: "bool" },
    ],
    fields: [
      { key: "nombre", label: "Nombre", type: "text", required: true },
      { key: "orden", label: "Orden", type: "number", required: true, default: 0 },
      { key: "activa", label: "Activa", type: "toggle", default: true },
    ],
  },
  {
    key: "persona-conceptos",
    label: "Conceptos por persona",
    singular: "Concepto de persona",
    endpoint: "/persona-conceptos",
    scope: "politica",
    columns: [
      { key: "persona_id", label: "Persona", type: "ref", ref: personaRef },
      { key: "tipo_concepto_id", label: "Concepto", type: "ref", ref: tipoConceptoRef },
      { key: "importe_anual", label: "Importe anual", type: "money" },
    ],
    fields: [
      { key: "persona_id", label: "Persona", type: "ref", ref: personaRef, required: true },
      { key: "tipo_concepto_id", label: "Tipo de concepto", type: "ref", ref: tipoConceptoRef, required: true },
      { key: "importe_anual", label: "Importe anual (€)", type: "decimal", required: true, default: 0 },
    ],
  },
];

